import { IChangeAction, IChanges, IDocumentTable, IRow } from '@models/Forms/IForms';
import { IField, IFieldElem, IFormData } from '@models/IFormData';
import { safeFormatDate } from '@utils/date';
import { indirectEval } from '@utils/scriptEval';
import { FieldValues } from 'react-hook-form';
import { IRequiredValidation } from '@models/actions/IRequiredValidation';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';

export const wrapSpecialChars = (value: string) => {
    return value.replace(/\\n/g, '\\n');
};

export const formatFieldValueByType = (field: any) => {
    switch (typeof field) {
        case 'string':
            return "'" + wrapSpecialChars(field) + "'";
        case 'number':
            return '' + field + '';
        default:
            return '' + field + '';
    }
};

export const containsValue = (fieldPath: string, data?: Record<string, IFieldElem>) => {
    return data && data[fieldPath];
};

export const getValueString = (fieldPath: string, data?: Record<string, IFieldElem>) => {
    if (data) {
        const field = data[fieldPath];
        if (field?.value !== undefined && field?.value != null) {
            if (isIsoDateString(field?.value)) {
                let val = new Date(field?.value);
                return safeFormatDate(val, 'DD.MM.YYYY HH:mm');
            } else {
                return field?.value.toString();
            }
        } else {
            return '';
        }
    }
    return '';
};

export const convertListToMap = (list: IField[], pref: string = '') => {
    const map = {} as Record<string, IFieldElem>;
    let index = 0;
    if (list) {
        for (const ele of list) {
            if (ele?.name) {
                map[pref + ele.name] = {
                    index: index,
                    name: `fields.[${index}].value`,
                    value: ele.value,
                };
                index++;
            }
        }
    }
    return map;
};

export const convertObjToMap = (object: any) => {
    const map = {} as Record<string, IFieldElem>;

    if (object) {
        const keysObj = Object.keys(object);
        for (let index = 0; index < keysObj.length; index++) {
            let keyName = keysObj[index];
            map[keyName] = {
                index: index,
                name: `fields.[${index}].value`,
                value: object[keyName],
            };
        }
    }
    return map;
};

// Схема и тела вызываемых actions должны давать одинаковый набор полей при init и submit.
export const getFieldKeysByScheme = (
    rows: IRow[] | undefined,
    onlyEdit = false,
    changeActions: readonly IChangeAction[] = [],
): string[] => {
    const result = new Set<string>();
    const add = (keys: Array<string | undefined>) => keys.forEach((key) => { if (key) result.add(key); });
    const changes = (value?: IChanges) => add(getFieldKeysByChanges(value, onlyEdit, changeActions));
    const visitTable = (table: IDocumentTable) => {
        add([table.key]);
        [table.changes, table.onDeletedRows, table.onCopiedRows, table.onFormRowEdited].forEach(changes);
        for (const column of [
            ...(table.tableColumn ?? []), ...(table.tableColumnDict ?? []), ...(table.tableColumnAbook ?? []),
            ...(table.tableColumnCalc ?? []), ...(table.tableColumnAutoComplete ?? []), ...(table.tableColumnExDataSource ?? []),
        ]) {
            if ('changes' in column) changes(column.changes);
        }
        table.tables?.forEach(visitTable);
    };
    for (const row of rows ?? []) {
        for (const column of row.columns ?? []) {
            for (const control of [
                ...(column.abooks ?? []), ...(column.calcs ?? []), ...(column.dicts ?? []),
                ...(column.fields ?? []), ...(column.autoCompletes ?? []),
            ]) {
                add([control.key]);
                changes(control.changes);
                if ('setValues' in control) add(control.setValues?.sets?.map((set) => set.key) ?? []);
            }
            column.tables?.forEach(visitTable);
            add(getFieldKeysByScheme(column.rows, onlyEdit, changeActions));
            for (const nested of [...(column.blocks ?? []), ...(column.editInModals ?? [])]) {
                add(getFieldKeysByScheme(nested.rows?.row, onlyEdit, changeActions));
            }
            if (!onlyEdit) {
                add(column.dicts?.flatMap((dict) => dict.formValues?.formValue?.map((value) => value.attr) ?? []) ?? []);
            }
        }
    }
    return [...result];
};

// Обходим только достижимые actions. Цикл не мешает собрать поля; ошибку вызова проверяет runtime.
export const getFieldKeysByChanges = (
    changes: IChanges | undefined,
    onlyEdit = false,
    changeActions: readonly IChangeAction[] = [],
): string[] => {
    const result = new Set<string>();
    const actions = new Map(changeActions.map((action) => [action.name, action]));
    const visited = new Set<string>();
    const add = (key?: string) => { if (key) result.add(key); };
    const visit = (block?: IChanges) => {
        for (const change of block?.change ?? []) {
            const sets = change.setValues;
            sets?.sets?.forEach((set) => add(set.key));
            for (const item of [...(sets?.addRows ?? []), ...(sets?.updateRows ?? []),
                ...(sets?.deleteRows ?? []), ...(sets?.clearTables ?? [])]) add(item.table);
            for (const procedure of sets?.sqlProcedures ?? []) {
                add(procedure.target);
                procedure.mapping?.forEach((mapping) => add(mapping.target));
            }
            visit(change.changes);
            visit(change.elseChanges?.changes);
            const name = change.callAction?.name;
            if (name && !visited.has(name)) {
                visited.add(name);
                visit(actions.get(name)?.changes);
            }
        }
    };
    // onlyEdit сохранён в API: для changes учитываются все записываемые поля в обоих режимах.
    void onlyEdit;
    visit(changes);
    return [...result];
};

// Выбор данных только для полей из схемы и вызываемых ею actions.
export const getFormDataByScheme = (
    rows: IRow[], data: FieldValues, onlyEdit = false, changeActions: readonly IChangeAction[] = [],
): IFormData => {
    const keys = getFieldKeysByScheme(rows, onlyEdit, changeActions);
    const map = new Map((data as IField[]).map(({ name, value }) => [name, { name, value }]));
    return { fields: keys.flatMap((key) => { const field = map.get(key); return field ? [field] : []; }) };
};

export const getEmptyFormDataByScheme = (rows: IRow[], changeActions: readonly IChangeAction[] = []): IFormData => ({
    fields: getFieldKeysByScheme(rows, false, changeActions).map((name) => ({ name, value: null })),
});

export const canVisibility = (rules?: string, data?: Record<string, IFieldElem>) => {
    const regex = /\{.*?\}/g;
    if (rules) {
        let result = rules; // or use regex without global flag gor replacing in original var

        if (rules && data) {
            let m;

            while ((m = regex.exec(rules)) !== null) {
                // eslint-disable-next-line no-loop-func
                m.forEach((match) => {
                    let fName = match.replace('{', '').replace('}', '');
                    let fValue = data[fName];
                    let fieldVal = fValue ? formatFieldValueByType(fValue.value) : "''";
                    result = result?.replaceAll(match, fieldVal!);
                });
            }
        }
        return indirectEval(result!);
    }
    return true;
};

export const parseTableDisplayFormat = (
    format?: string,
    data?: Record<string, IFieldElem>,
    joinSeparator: string = ', ',
) => {
    const regex = /\[.*?\]/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            if (matches.length > 0) {
                let match = matches[0];
                let rows: string[] = [];
                let columnName = match.replace('[', '').replace(']', '');
                let tableFieldName = format.replaceAll(match, '');
                let tableField = data[tableFieldName];
                if (tableField) {
                    let table = data[tableFieldName].value as any[];
                    if (!Array.isArray(table)) return '';
                    table.forEach((row) => {
                        rows.push(row[columnName]);
                    });
                    return rows.join(joinSeparator);
                }
            }
        }
    }
    return format;
};

export const isEmptyDisplayValue = (value: any) => value === undefined || value === null || value === '';

export const areAllDisplayValuesEmpty = (values?: any[]) =>
    !!values?.length && values.every((v) => isEmptyDisplayValue(v));

const toEvalLiteral = (value: any) => {
    if (value === undefined || value === null || value === '') {
        return "''";
    }

    if (value instanceof Date) {
        return value.getTime().toString();
    }

    if (typeof value === 'string') {
        return JSON.stringify(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch {
        return JSON.stringify(String(value));
    }
};

export const evalDisplayExpression = (expression?: string) => {
    if (!expression) {
        return '';
    }

    try {
        const result = indirectEval(expression);
        if (result === undefined || result === null) {
            return '';
        }

        return String(result);
    } catch (error) {
        console.error('Display format eval failed', expression, error);
        return '';
    }
};

export const parseDisplayFormat = (
    format?: string,
    data?: Record<string, IFieldElem>,
    joinSeparator: string = ',',
    evalDisplayFormat: boolean = false,
) => {
    const regex = /\{.*?\}/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            if (matches.length > 0) {
                if (evalDisplayFormat) {
                    let values: any[] = [];
                    matches.forEach((match) => {
                        let fName = match.replace('{', '').replace('}', '');
                        let fieldVal: any;
                        if (fName.indexOf('[') != -1) {
                            fieldVal = parseTableDisplayFormat(fName, data, joinSeparator) ?? '';
                        } else {
                            fieldVal = data[fName]?.value ?? '';
                        }
                        values.push(fieldVal);
                        format = format?.replaceAll(match, toEvalLiteral(fieldVal));
                    });
                    if (areAllDisplayValuesEmpty(values)) {
                        return { text: '', values: [] } as { text: string; values: string[] };
                    }

                    const text = getDisplayValue(evalDisplayExpression(format));
                    return {
                        text: text ?? '',
                        values: text ? [text] : [],
                    };
                }

                let values: string[] = [];
                matches.forEach((match) => {
                    let fName = match.replace('{', '').replace('}', '');
                    if (fName.indexOf('[') != -1) {
                        // TODO Rework this
                        format = parseTableDisplayFormat(fName, data, joinSeparator);
                        if (format) {
                            values.push(format);
                        }
                    } else {
                        let fValue = data[fName];
                        let fieldVal =
                            fValue && fValue.value && fValue.value != null && fValue.value !== ''
                                ? fValue.value?.toString()
                                : '';
                        values.push(fieldVal);
                        format = format?.replaceAll(match, fieldVal!);
                    }
                });
                if (areAllDisplayValuesEmpty(values)) {
                    return { text: '', values: [] } as { text: string; values: string[] };
                }
            }
        }
        return {
            text: format.replace(/\|/g, joinSeparator),
            values: format.split('|').sort((a, b) => {
                const textA = a.toUpperCase();
                const textB = b.toUpperCase();
                return textA < textB ? -1 : textA > textB ? 1 : 0;
            }),
        };
    }

    return { text: '', values: [] } as { text: string; values: string[] };
};

export const parseMultilineDisplayFormat = (
    format?: string,
    data?: Record<string, IFieldElem>,
    evalDisplayFormat: boolean = false,
) => {
    const matches = format ? Array.from(format.matchAll(/\{.*?\}/g)).map((match) => match[0]) : [];
    if (!format || !data || matches.length === 0 || matches.some((match) => match.includes('['))) {
        return parseDisplayFormat(format, data, '\r\n', evalDisplayFormat);
    }

    const fieldNames = Array.from(new Set(matches.map((match) => match.slice(1, -1))));
    const valuesByField = fieldNames.map((fieldName) => {
        const value = data[fieldName]?.value;
        const values = Array.isArray(value)
            ? value.map((item) => item?.toString() ?? '')
            : value === undefined || value === null
              ? []
              : value.toString().split('|');
        return { fieldName, values };
    });
    const rowCount = Math.max(0, ...valuesByField.map(({ values }) => values.length));
    const rows: string[] = [];

    for (let index = 0; index < rowCount; index++) {
        const rowData = { ...data };
        let hasValue = false;

        valuesByField.forEach(({ fieldName, values }) => {
            const value = values[index] ?? '';
            hasValue ||= value !== '';
            rowData[fieldName] = { ...data[fieldName], value } as IFieldElem;
        });

        if (hasValue) {
            const row = parseDisplayFormat(format, rowData, ',', evalDisplayFormat).text;
            if (row !== '') {
                rows.push(row);
            }
        }
    }

    return {
        text: rows.join('\r\n'),
        values: rows,
    };
};

export const parseDisplayFormatCell = (format?: string, data?: any, evalDisplayFormat: boolean = false) => {
    const regex = /\{.*?\}/g;
    if (format) {
        if (format && data) {
            const matches = Array.from(format.matchAll(regex)).map((match) => match[0]);
            if (evalDisplayFormat) {
                let values: any[] = [];
                matches.forEach((match) => {
                    let fName = match.replace('{', '').replace('}', '');
                    let fieldVal = data[fName] ?? '';
                    values.push(fieldVal);
                    format = format?.replaceAll(match, toEvalLiteral(fieldVal));
                });
                if (areAllDisplayValuesEmpty(values)) {
                    return '';
                }

                return getDisplayValue(evalDisplayExpression(format));
            }

            let values: string[] = [];
            matches.forEach((match) => {
                let fName = match.replace('{', '').replace('}', '');
                let fValue = data[fName];
                let fieldVal = fValue ? fValue.toString() : '';
                values.push(fieldVal);
                format = format?.replaceAll(match, fieldVal!);
            });
            if (areAllDisplayValuesEmpty(values)) {
                return '';
            }
        }
        return format.replace(/\|/g, ',');
    }
    return '';
};

export const getValueBoolean = (value?: any) => {
    if (value !== undefined && value !== null) {
        return value === 1 || value === '1' ? true : false;
    } else {
        return undefined;
    }
};

const isoDateFormat = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)((-|\+(\d{2}):(\d{2})|Z)?)$/;
const isoDateNoTimeFormat = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isIsoDateString = (value: any) => {
    return value && typeof value === 'string' && isoDateFormat.test(value);
};

export const isIsoDateNoTimeString = (value: any) => {
    return value && typeof value === 'string' && isoDateNoTimeFormat.test(value);
};

export const getValueDates = (value: any) => {
    if (isIsoDateString(value) || isIsoDateNoTimeString(value)) {
        return new Date(value);
    } else {
        return value;
    }
};

export const getDisplayValue = (value: any) => {
    if (value) {
        let str = value.trim();
        str = str.replaceAll('undefined', '').trim();
        if (str === '' || str === "''" || str === ',') {
            return '';
        } else {
            return value;
        }
    } else {
        return value;
    }
};

export const checkAttachValidation = (conditions: IRequiredValidation[], files?: ISelectedFiles): string[] => {
    let result: string[] = [];

    if (files?.files === undefined || files?.files.length === 0) return result;

    for (let index = 0; index < conditions.length; index++) {
        let condition = conditions[index];
        let all = condition.type === undefined || condition.type === 'all';

        for (let index = 0; index < files?.attachProperties.length; index++) {
            const element = files?.attachProperties[index];
            let map = convertObjToMap(element);
            let checkResult = canVisibility(condition.condition, map);
            if (!checkResult && all) {
                result.push(condition.message);
                break;
            }
            if (checkResult && !all) {
                break;
            }
            if (!checkResult && !all && index === files?.attachProperties.length - 1) {
                result.push(condition.message);
            }
        }
    }
    return result;
};
